import React, { useState, useEffect } from 'react';
import { getVendorAnalytics, getVendorAnalyticsDetail, getInventories, getItems, exportVendorAnalyticsExcel } from '../lib/api';
import { downloadBlob } from '../lib/downloadHelper';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Truck,
  Loader2,
  DollarSign,
  ShoppingCart,
  Hash,
  Eye,
  ChevronUp,
  ChevronDown,
  Download,
  Printer
} from 'lucide-react';

const VendorAnalytics = () => {
  const [vendorData, setVendorData] = useState([]);
  const [inventories, setInventories] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInventory, setSelectedInventory] = useState('all');
  const [selectedItem, setSelectedItem] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortBy, setSortBy] = useState('total_spend');
  const [sortOrder, setSortOrder] = useState('desc');

  // Detail dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorDetail, setVendorDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [exportingVA, setExportingVA] = useState(false);

  useEffect(() => {
    Promise.all([getInventories(), getItems()]).then(([invRes, itemsRes]) => {
      setInventories(invRes.data);
      setAllItems(itemsRes.data);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInventory, selectedItem, startDate, endDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedInventory !== 'all') params.inventory_id = selectedInventory;
      if (selectedItem !== 'all') params.item_id = selectedItem;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await getVendorAnalytics(params);
      setVendorData(res.data);
    } catch (err) {
      toast.error('Failed to load vendor analytics');
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (vendor) => {
    setSelectedVendor(vendor);
    setDetailOpen(true);
    setLoadingDetail(true);
    try {
      const params = {};
      if (selectedInventory !== 'all') params.inventory_id = selectedInventory;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await getVendorAnalyticsDetail(vendor.vendor_id, params);
      setVendorDetail(res.data);
    } catch (err) {
      toast.error('Failed to load vendor details');
    } finally {
      setLoadingDetail(false);
    }
  };

  const sortedVendors = [...vendorData].sort((a, b) => {
    let aVal, bVal;
    if (sortBy === 'vendor_name') {
      return sortOrder === 'asc' 
        ? a.vendor_name.localeCompare(b.vendor_name) 
        : b.vendor_name.localeCompare(a.vendor_name);
    }
    aVal = a[sortBy]; bVal = b[sortBy];
    return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
  });

  const totalSpend = vendorData.reduce((sum, v) => sum + v.total_spend, 0);
  const totalTransactions = vendorData.reduce((sum, v) => sum + v.transaction_count, 0);

  const handleSort = (col) => {
    if (sortBy === col) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortOrder('desc'); }
  };

  const SortIcon = ({ column }) => {
    if (sortBy !== column) return null;
    return sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  return (
    <div className="space-y-6 animate-slide-in" data-testid="vendor-analytics-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="heading-2 text-[#1F2937]">Vendor Analytics</h1>
          <p className="text-[#6B7280]">Analyze purchasing patterns and vendor spending</p>
        </div>
        <div className="flex gap-2 print:hidden">
          <Button onClick={async () => {
            setExportingVA(true);
            try {
              const params = {};
              if (selectedInventory !== 'all') params.inventory_id = selectedInventory;
              if (startDate) params.start_date = startDate;
              if (endDate) params.end_date = endDate;
              const res = await exportVendorAnalyticsExcel(params);
              downloadBlob(res.data, `vendor_analytics_${new Date().toISOString().split('T')[0]}.xlsx`);
            } catch (e) { alert('Export failed.'); } finally { setExportingVA(false); }
          }} disabled={exportingVA || vendorData.length === 0}
            className="gap-2 bg-green-600 hover:bg-green-700 text-white rounded-lg" data-testid="export-va-btn">
            {exportingVA ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download size={18} />}
            Export Excel
          </Button>
          <Button onClick={() => window.print()} variant="outline"
            className="gap-2 rounded-lg border-[#0776BD] text-[#0776BD]" data-testid="print-va-btn">
            <Printer size={18} /> Print
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-[#E6F2FA] rounded-xl">
                <Truck size={24} className="text-[#0776BD]" />
              </div>
              <div>
                <p className="text-sm font-medium text-[#6B7280] uppercase tracking-wide">Active Vendors</p>
                <p className="text-3xl font-bold font-heading text-[#1F2937]" data-testid="vendor-count">{vendorData.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-[#FFF8E1] rounded-xl">
                <DollarSign size={24} className="text-[#F0C500]" />
              </div>
              <div>
                <p className="text-sm font-medium text-[#6B7280] uppercase tracking-wide">Total Spend</p>
                <p className="text-3xl font-bold font-heading text-[#1F2937]" data-testid="total-vendor-spend">${totalSpend.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-xl">
                <ShoppingCart size={24} className="text-green-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-[#6B7280] uppercase tracking-wide">Total Purchases</p>
                <p className="text-3xl font-bold font-heading text-[#1F2937]" data-testid="total-purchases">{totalTransactions}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <Select value={selectedInventory} onValueChange={setSelectedInventory}>
              <SelectTrigger className="w-full md:w-[200px] h-12 rounded-lg border-gray-300" data-testid="va-inventory-filter">
                <SelectValue placeholder="All Inventories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Inventories</SelectItem>
                {inventories.map(inv => (
                  <SelectItem key={inv.id} value={inv.id}>{inv.inventory_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedItem} onValueChange={setSelectedItem}>
              <SelectTrigger className="w-full md:w-[220px] h-12 rounded-lg border-gray-300" data-testid="va-item-filter">
                <SelectValue placeholder="All Items" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Items</SelectItem>
                {allItems.map(item => (
                  <SelectItem key={item.id} value={item.id}>{item.item_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-2 flex-1">
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="h-12 rounded-lg border-gray-300 text-sm"
                data-testid="va-start-date"
              />
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="h-12 rounded-lg border-gray-300 text-sm"
                data-testid="va-end-date"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Vendor Table */}
      <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
        <CardHeader>
          <CardTitle className="font-heading text-lg text-[#1F2937] flex items-center gap-2">
            <Truck size={20} className="text-[#0776BD]" />
            Vendor Spending ({sortedVendors.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#0776BD]" />
            </div>
          ) : sortedVendors.length === 0 ? (
            <div className="py-12 text-center text-[#6B7280]">
              <Truck size={48} className="mx-auto mb-4 opacity-50" />
              <p>No vendor purchase data found</p>
              <p className="text-sm">Data appears here when restocking from vendors</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F5F6F7]">
                    <TableHead className="font-semibold text-[#1F2937] cursor-pointer hover:bg-gray-200" onClick={() => handleSort('vendor_name')}>
                      <div className="flex items-center gap-1">Vendor <SortIcon column="vendor_name" /></div>
                    </TableHead>
                    <TableHead className="font-semibold text-[#1F2937] text-right cursor-pointer hover:bg-gray-200" onClick={() => handleSort('total_spend')}>
                      <div className="flex items-center justify-end gap-1">Total Spend <SortIcon column="total_spend" /></div>
                    </TableHead>
                    <TableHead className="font-semibold text-[#1F2937] text-center cursor-pointer hover:bg-gray-200" onClick={() => handleSort('total_quantity')}>
                      <div className="flex items-center justify-center gap-1">Total Qty <SortIcon column="total_quantity" /></div>
                    </TableHead>
                    <TableHead className="font-semibold text-[#1F2937] text-center cursor-pointer hover:bg-gray-200" onClick={() => handleSort('transaction_count')}>
                      <div className="flex items-center justify-center gap-1">Transactions <SortIcon column="transaction_count" /></div>
                    </TableHead>
                    <TableHead className="font-semibold text-[#1F2937] text-right">Spend Share</TableHead>
                    <TableHead className="font-semibold text-[#1F2937] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedVendors.map((vendor) => (
                    <TableRow key={vendor.vendor_id} className="hover:bg-[#F5F6F7]" data-testid={`vendor-analytics-row-${vendor.vendor_id}`}>
                      <TableCell>
                        <span className="font-medium text-[#1F2937]">{vendor.vendor_name}</span>
                      </TableCell>
                      <TableCell className="text-right font-bold text-[#1F2937] text-lg">
                        ${vendor.total_spend.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-center text-[#6B7280]">{vendor.total_quantity}</TableCell>
                      <TableCell className="text-center">
                        <Badge className="bg-[#E6F2FA] text-[#0776BD] border-0">{vendor.transaction_count}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-20 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-[#0776BD] h-2 rounded-full"
                              style={{ width: `${totalSpend > 0 ? (vendor.total_spend / totalSpend * 100) : 0}%` }}
                            />
                          </div>
                          <span className="text-sm text-[#6B7280] w-12 text-right">
                            {totalSpend > 0 ? (vendor.total_spend / totalSpend * 100).toFixed(0) : 0}%
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDetail(vendor)}
                          className="hover:bg-[#E6F2FA] text-[#0776BD] gap-1"
                          data-testid={`view-vendor-analytics-${vendor.vendor_id}`}
                        >
                          <Eye size={16} />
                          Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Vendor Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl rounded-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#1F2937] flex items-center gap-2">
              <Truck className="text-[#0776BD]" />
              {selectedVendor?.vendor_name} - Purchase History
            </DialogTitle>
          </DialogHeader>

          {loadingDetail ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#0776BD]" />
            </div>
          ) : vendorDetail ? (
            <div className="space-y-4">
              {/* Vendor Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-[#E6F2FA] rounded-lg text-center">
                  <p className="text-sm text-[#6B7280]">Total Spend</p>
                  <p className="text-2xl font-bold text-[#0776BD]" data-testid="vendor-detail-spend">${vendorDetail.total_spend.toFixed(2)}</p>
                </div>
                <div className="p-4 bg-[#F5F6F7] rounded-lg text-center">
                  <p className="text-sm text-[#6B7280]">Total Qty</p>
                  <p className="text-2xl font-bold text-[#1F2937]">{vendorDetail.total_quantity}</p>
                </div>
                <div className="p-4 bg-[#F5F6F7] rounded-lg text-center">
                  <p className="text-sm text-[#6B7280]">Transactions</p>
                  <p className="text-2xl font-bold text-[#1F2937]">{vendorDetail.transaction_count}</p>
                </div>
              </div>

              {/* Items Purchased */}
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F5F6F7]">
                    <TableHead className="font-semibold text-[#1F2937]">Item</TableHead>
                    <TableHead className="font-semibold text-[#1F2937] text-center">Qty Purchased</TableHead>
                    <TableHead className="font-semibold text-[#1F2937] text-right">Last Unit Price</TableHead>
                    <TableHead className="font-semibold text-[#1F2937] text-right">Total Spend</TableHead>
                    <TableHead className="font-semibold text-[#1F2937] text-center">Orders</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vendorDetail.items.map((item, idx) => (
                    <TableRow key={item.item_id || `vi-${idx}`}>
                      <TableCell>
                        <div className="font-medium text-[#1F2937]">{item.item_name}</div>
                        <div className="text-xs text-[#0776BD] font-mono">({item.internal_code})</div>
                      </TableCell>
                      <TableCell className="text-center text-[#1F2937]">{item.total_quantity}</TableCell>
                      <TableCell className="text-right text-[#6B7280]">${item.last_unit_price.toFixed(2)}</TableCell>
                      <TableCell className="text-right font-medium text-[#1F2937]">${item.total_spend.toFixed(2)}</TableCell>
                      <TableCell className="text-center">
                        <Badge className="bg-gray-100 text-gray-600 border-0">{item.transaction_count}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="border-t-2 border-[#0776BD]">
                    <TableCell colSpan={3} className="text-right font-bold text-[#1F2937]">Total</TableCell>
                    <TableCell className="text-right font-bold text-xl text-[#0776BD]">${vendorDetail.total_spend.toFixed(2)}</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailOpen(false)} className="rounded-lg">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VendorAnalytics;
